/* ============================================================
   Talention — SVPT embebido: catálogos, semilla de ejemplo y helpers

   Un espacio de trabajo por empresa: { metodo, departments, levels,
   positions, factors, scales, valuations, ministerio }. La empresa activa
   la fija registro (session.clienteId); aquí no hay multiempresa. El
   método Talention usa factors/scales/valuations; el método Ministerio
   vive en ministerio.js y guarda sus respuestas en `ministerio`. Ver
   src/svpt/store.jsx.
   ============================================================ */

import {
  crearMinisterioVacio,
  podarRespuestasMinisterio,
  agrupacionMinisterio,
  progresoMinisterio,
  otroCompleto,
  rangoDeTramo,
  CODIGOS_OTROS,
} from "./ministerio.js";

const FACTOR_GROUPS = [
  {
    id: "knowledge",
    name: "Conocimiento y Aptitudes",
    short: "Conocimiento",
    tint: "tint-knowledge",
    color: "#0a84ff",
    icon: "brain",
    iconBg: "#dfeeff",
    description: "Formación, conocimientos técnicos, idiomas y capacidades cognitivas requeridas",
    defaultSVPT: 30,
  },
  {
    id: "responsibility",
    name: "Responsabilidad",
    short: "Responsabilidad",
    tint: "tint-responsibility",
    color: "#af52de",
    icon: "shield",
    iconBg: "#f1e4fa",
    description: "Responsabilidad sobre personas, recursos, información o resultados",
    defaultSVPT: 35,
  },
  {
    id: "effort",
    name: "Esfuerzo",
    short: "Esfuerzo",
    tint: "tint-effort",
    color: "#ec7a15",
    icon: "zap",
    iconBg: "#ffe9d1",
    description: "Esfuerzo físico y mental requerido en el desempeño del puesto",
    defaultSVPT: 25,
  },
  {
    id: "conditions",
    name: "Condiciones de trabajo",
    short: "Condiciones",
    tint: "tint-conditions",
    color: "#34c759",
    icon: "factory",
    iconBg: "#e3f8e8",
    description: "Entorno, riesgos, horarios y condiciones físicas del puesto",
    defaultSVPT: 10,
  },
];

/* Tipos de subfactor.
   - escala:  varios niveles ordenados de menor a mayor exigencia.
   - binario: dos niveles, no lo requiere / lo requiere. */
const ITEM_TYPES = [
  { id: "escala",  name: "Escala de niveles", desc: "Varios niveles ordenados de menor a mayor exigencia" },
  { id: "binario", name: "Sí / No",           desc: "Solo dos respuestas: no se requiere o se requiere" },
];

const GENDERS = [
  { id: "NEUTRO",    name: "Neutro",    desc: "Sin sesgo de género asociado" },
  { id: "MASCULINO", name: "Masculino", desc: "Históricamente asociado a puestos masculinizados" },
  { id: "FEMENINO",  name: "Femenino",  desc: "Históricamente asociado a puestos feminizados" },
];

/* Niveles genéricos de 4 escalones, para subfactores del catálogo que aún
   no tienen niveles propios definidos. */
const GENERIC_LEVELS = [
  { id: "L0", label: "No se requiere", desc: "El puesto no se ve afectado" },
  { id: "L1", label: "Bajo",           desc: "De forma puntual" },
  { id: "L2", label: "Medio",          desc: "Con frecuencia" },
  { id: "L3", label: "Alto",           desc: "De forma permanente" },
];

const BINARY_LEVELS = [
  { id: "L0", label: "No", desc: "El puesto no lo requiere" },
  { id: "L1", label: "Sí", desc: "El puesto lo requiere" },
];

/* Catálogo de subfactores por grupo. Los que llegan con `levels: []` están
   en el catálogo pero todavía no son valorables: hay que definirles niveles
   (desde el editor de subfactores o aplicando un estándar). */
const FACTOR_ITEMS = {
  knowledge: [
    {
      id: "k-formacion", name: "Formación académica", weight: 50, gender: "NEUTRO", selected: true, type: "escala",
      desc: "Nivel mínimo de enseñanza convencional para el puesto",
      levels: [
        { id: "L0", label: "No se requiere", desc: "No se requieren estudios específicos" },
        { id: "L1", label: "Primarios", desc: "Educación primaria / hasta 6º de EGB" },
        { id: "L2", label: "Secundarios", desc: "Educación secundaria obligatoria" },
        { id: "L3", label: "Bachillerato / FP", desc: "Bachillerato / 2º de FP de Grado II / COU" },
        { id: "L4", label: "Diplomatura / Grado", desc: "Diplomaturas / Ingenierías técnicas / Grado" },
        { id: "L5", label: "Licenciatura / Máster", desc: "Licenciaturas / Ingenierías / Máster" },
        { id: "L6", label: "Postgrado / Doctorado", desc: "Postgrado / Doctorado / MBA o similar" },
      ],
    },
    {
      id: "k-idiomas", name: "Conocimiento de idiomas", weight: 20, gender: "NEUTRO", selected: true, type: "escala",
      desc: "Nivel mínimo de idioma para el puesto",
      levels: [
        { id: "L0", label: "No se requiere", desc: "No es necesario el conocimiento de un idioma" },
        { id: "L1", label: "Bajo", desc: "Nivel básico (A2)" },
        { id: "L2", label: "Medio", desc: "Nivel intermedio (B1) o básico de más de uno" },
        { id: "L3", label: "Alto", desc: "Nivel alto (B2-C1) o intermedio de más de uno" },
        { id: "L4", label: "Muy alto", desc: "Nivel muy alto (C2) o alto de más de uno" },
      ],
    },
    {
      id: "k-liderazgo", name: "Capacidad de liderazgo", weight: 10, gender: "MASCULINO", selected: true, type: "escala",
      desc: "Capacidad para influir, motivar y organizar acciones",
      levels: [
        { id: "L0", label: "No se requiere", desc: "No requiere capacidad de liderazgo" },
        { id: "L1", label: "Bajo", desc: "Puntualmente, en proyectos ocasionales (<30%)" },
        { id: "L2", label: "Medio", desc: "Con frecuencia, mitad de la jornada" },
        { id: "L3", label: "Alto", desc: "Liderazgo permanente" },
      ],
    },
    {
      id: "k-iniciativa", name: "Iniciativa", weight: 5, gender: "NEUTRO", selected: true, type: "escala",
      desc: "Capacidad para planear, idear o emprender propuestas",
      levels: [
        { id: "L0", label: "No se requiere" },
        { id: "L1", label: "Baja" },
        { id: "L2", label: "Media" },
        { id: "L3", label: "Alta" },
      ],
    },
    {
      id: "k-maquinaria", name: "Manejo de maquinaria y/o equipos", weight: 15, gender: "NEUTRO", selected: true, type: "escala",
      desc: "Habilidad para operar equipos o maquinaria propia del puesto",
      levels: [
        { id: "L0", label: "No se requiere" },
        { id: "L1", label: "Básico" },
        { id: "L2", label: "Medio" },
        { id: "L3", label: "Avanzado" },
      ],
    },
    { id: "k-especificos", name: "Conocimientos específicos", weight: 0, gender: "NEUTRO", selected: false, type: "escala", levels: [] },
    { id: "k-dificultad", name: "Dificultad técnica", weight: 0, gender: "NEUTRO", selected: false, type: "escala", levels: [] },
    { id: "k-informaticos", name: "Conocimientos informáticos", weight: 0, gender: "NEUTRO", selected: false, type: "escala", levels: [] },
    { id: "k-sociales", name: "Habilidades sociales", weight: 0, gender: "FEMENINO", selected: false, type: "escala", levels: [] },
    { id: "k-creatividad", name: "Creatividad", weight: 0, gender: "NEUTRO", selected: false, type: "escala", levels: [] },
    { id: "k-comunicacion", name: "Habilidades de comunicación", weight: 0, gender: "NEUTRO", selected: false, type: "escala", levels: [] },
    { id: "k-fisicas", name: "Competencias físicas", weight: 0, gender: "MASCULINO", selected: false, type: "escala", levels: [] },
    { id: "k-destreza", name: "Habilidad manual-destreza", weight: 0, gender: "NEUTRO", selected: false, type: "escala", levels: [] },
    { id: "k-precision", name: "Habilidad manual-precisión", weight: 0, gender: "NEUTRO", selected: false, type: "escala", levels: [] },
    { id: "k-artesanal", name: "Trabajo artesanal", weight: 0, gender: "NEUTRO", selected: false, type: "escala", levels: [] },
    { id: "k-experiencia", name: "Experiencia requerida", weight: 0, gender: "NEUTRO", selected: false, type: "escala", levels: [] },
    { id: "k-actualizacion", name: "Actualización constante", weight: 0, gender: "NEUTRO", selected: false, type: "escala", levels: [] },
    { id: "k-cobertura", name: "Complejidad en la cobertura el puesto", weight: 0, gender: "NEUTRO", selected: false, type: "escala", levels: [] },
  ],
  responsibility: [
    {
      id: "r-personas", name: "Personas a su cargo", weight: 100, gender: "NEUTRO", selected: true, type: "escala",
      desc: "Número y nivel de personas reportando al puesto",
      levels: [
        { id: "L0", label: "Ninguna", desc: "No tiene personas a su cargo" },
        { id: "L1", label: "1-3 personas", desc: "Equipo pequeño" },
        { id: "L2", label: "4-10 personas", desc: "Equipo mediano" },
        { id: "L3", label: "11-30 personas", desc: "Equipo grande" },
        { id: "L4", label: "Más de 30", desc: "Departamento completo" },
      ],
    },
    { id: "r-resultados", name: "Repercusión en resultados de la organización", weight: 0, gender: "NEUTRO", selected: false, type: "escala", levels: [] },
    { id: "r-maquinaria", name: "Responsabilidad sobre maquinaria", weight: 0, gender: "MASCULINO", selected: false, type: "escala", levels: [] },
    { id: "r-seguridad", name: "Seguridad de otras personas", weight: 0, gender: "NEUTRO", selected: false, type: "escala", levels: [] },
    { id: "r-confidencial", name: "Confidencialidad", weight: 0, gender: "NEUTRO", selected: false, type: "escala", levels: [] },
    { id: "r-autonomia", name: "Nivel de autonomía y responsabilidad", weight: 0, gender: "NEUTRO", selected: false, type: "escala", levels: [] },
  ],
  effort: [
    {
      id: "e-mental", name: "Esfuerzo mental", weight: 50, gender: "NEUTRO", selected: true, type: "escala",
      desc: "Carga mental, atención y procesamiento requerido",
      levels: [
        { id: "L0", label: "Bajo", desc: "Tareas rutinarias, baja atención" },
        { id: "L1", label: "Medio", desc: "Atención continua, decisiones puntuales" },
        { id: "L2", label: "Alto", desc: "Toma de decisiones complejas" },
        { id: "L3", label: "Muy alto", desc: "Decisiones estratégicas continuas" },
      ],
    },
    {
      id: "e-concentracion", name: "Concentración", weight: 50, gender: "NEUTRO", selected: true, type: "escala",
      desc: "Nivel de concentración mantenida durante la jornada",
      levels: [
        { id: "L0", label: "Baja" },
        { id: "L1", label: "Media" },
        { id: "L2", label: "Alta" },
        { id: "L3", label: "Muy alta" },
      ],
    },
    { id: "e-emocional", name: "Esfuerzo emocional", weight: 0, gender: "FEMENINO", selected: false, type: "escala", levels: [] },
    { id: "e-fisico", name: "Esfuerzo físico: carga y descarga", weight: 0, gender: "MASCULINO", selected: false, type: "escala", levels: [] },
    { id: "e-posturas", name: "Posturas forzadas", weight: 0, gender: "NEUTRO", selected: false, type: "escala", levels: [] },
    { id: "e-repetitivos", name: "Movimientos repetitivos", weight: 0, gender: "NEUTRO", selected: false, type: "escala", levels: [] },
    { id: "e-pantallas", name: "Visualización de pantallas", weight: 0, gender: "NEUTRO", selected: false, type: "escala", levels: [] },
  ],
  conditions: [
    {
      id: "c-nocturnos", name: "Trabajos nocturnos", weight: 50, gender: "NEUTRO", selected: true, type: "escala",
      desc: "Frecuencia de trabajo en horario nocturno",
      levels: [
        { id: "L0", label: "Nunca" },
        { id: "L1", label: "Ocasionalmente" },
        { id: "L2", label: "Frecuentemente" },
        { id: "L3", label: "Habitualmente" },
      ],
    },
    {
      id: "c-viajes", name: "Viajes", weight: 50, gender: "MASCULINO", selected: true, type: "escala",
      desc: "Frecuencia de desplazamientos por trabajo",
      levels: [
        { id: "L0", label: "Nunca" },
        { id: "L1", label: "Ocasionalmente" },
        { id: "L2", label: "Frecuentemente" },
        { id: "L3", label: "Habitualmente" },
      ],
    },
    { id: "c-nocivos", name: "Manipulación de productos nocivos", weight: 0, gender: "NEUTRO", selected: false, type: "escala", levels: [] },
    { id: "c-ambientales", name: "Condiciones ambientales", weight: 0, gender: "NEUTRO", selected: false, type: "escala", levels: [] },
    { id: "c-psicosociales", name: "Condiciones psicosociales", weight: 0, gender: "NEUTRO", selected: false, type: "escala", levels: [] },
    { id: "c-vial", name: "Riesgo en seguridad vial", weight: 0, gender: "NEUTRO", selected: false, type: "escala", levels: [] },
    { id: "c-peligrosidad", name: "Peligrosidad del trabajo", weight: 0, gender: "NEUTRO", selected: false, type: "escala", levels: [] },
    { id: "c-no-laborables", name: "Trabajos días no laborables", weight: 0, gender: "NEUTRO", selected: false, type: "escala", levels: [] },
    { id: "c-dedicacion", name: "Dedicación e incompatibilidad", weight: 0, gender: "NEUTRO", selected: false, type: "escala", levels: [] },
  ],
};

/* ------------------------------------------------------------
   Estándares predefinidos de configuración de factores del método
   Talention.

   ATENCIÓN: los pesos y la selección de subfactores del estándar IOT son
   PROVISIONALES. Están puestos para que el mecanismo de aplicación funcione
   de punta a punta, pero hay que sustituirlos por los valores oficiales
   antes de usarlos con un cliente. Mientras `provisional` sea true, la
   interfaz lo avisa.

   El método del Ministerio no es un estándar: tiene su propio catálogo y
   su propio cálculo en ministerio.js.
   ------------------------------------------------------------ */
const FACTOR_STANDARDS = [
  {
    id: "iot",
    name: "IOT",
    fullName: "Estándar IOT",
    description: "Reparto orientado a puestos industriales, con más peso en responsabilidad y condiciones de trabajo.",
    provisional: true,
    groups: { knowledge: 30, responsibility: 30, effort: 25, conditions: 15 },
    items: {
      knowledge: [
        { id: "k-formacion", weight: 35 },
        { id: "k-experiencia", weight: 20, levels: GENERIC_LEVELS },
        { id: "k-maquinaria", weight: 20 },
        { id: "k-idiomas", weight: 10 },
        { id: "k-iniciativa", weight: 15 },
      ],
      responsibility: [
        { id: "r-personas", weight: 40 },
        { id: "r-maquinaria", weight: 25, levels: GENERIC_LEVELS },
        { id: "r-seguridad", weight: 20, levels: GENERIC_LEVELS },
        { id: "r-resultados", weight: 15, levels: GENERIC_LEVELS },
      ],
      effort: [
        { id: "e-fisico", weight: 30, levels: GENERIC_LEVELS },
        { id: "e-mental", weight: 30 },
        { id: "e-concentracion", weight: 20 },
        { id: "e-posturas", weight: 20, levels: GENERIC_LEVELS },
      ],
      conditions: [
        { id: "c-peligrosidad", weight: 30, levels: GENERIC_LEVELS },
        { id: "c-ambientales", weight: 25, levels: GENERIC_LEVELS },
        { id: "c-nocturnos", weight: 25 },
        { id: "c-nocivos", weight: 20, levels: BINARY_LEVELS, type: "binario" },
      ],
    },
  },
];

const SCALES = [
  { id: 1, name: "Escala 01", min: 701, max: 999, color: "#16a34a", label: "Directivo Senior" },
  { id: 2, name: "Escala 02", min: 651, max: 700, color: "#eab308", label: "Directivo" },
  { id: 3, name: "Escala 03", min: 601, max: 650, color: "#7c3aed", label: "Mando Superior" },
  { id: 4, name: "Escala 04", min: 551, max: 600, color: "#dc2626", label: "Mando Intermedio" },
  { id: 5, name: "Escala 05", min: 501, max: 550, color: "#0ea5e9", label: "Coord. Senior" },
  { id: 6, name: "Escala 06", min: 451, max: 500, color: "#ec4899", label: "Coordinador" },
  { id: 7, name: "Escala 07", min: 401, max: 450, color: "#f97316", label: "Especialista" },
  { id: 8, name: "Escala 08", min: 351, max: 400, color: "#1e3a8a", label: "Técnico Senior" },
  { id: 9, name: "Escala 09", min: 300, max: 350, color: "#06b6d4", label: "Técnico" },
  { id: 10, name: "Escala 10", min: 251, max: 300, color: "#059669", label: "Operativo Senior" },
  { id: 11, name: "Escala 11", min: 201, max: 250, color: "#a16207", label: "Operativo" },
  { id: 12, name: "Escala 12", min: 151, max: 200, color: "#6b7280", label: "Auxiliar" },
  { id: 13, name: "Escala 13", min: 0,   max: 150, color: "#9ca3af", label: "Aprendiz" },
];

/* Niveles jerárquicos genéricos: los comparten todas las empresas de partida. */
const LEVELS = [
  { id: 1, name: "Personal Directivo", description: "Alta dirección y dirección general", order: 1 },
  { id: 2, name: "Mandos intermedios", description: "Jefatura de área o departamento", order: 2 },
  { id: 3, name: "Personal Técnico", description: "Especialistas y técnicos cualificados", order: 3 },
  { id: 4, name: "Personal Administrativo", description: "Apoyo administrativo y operativo", order: 4 },
  { id: 5, name: "Personal Operativo", description: "Personal de planta y operaciones", order: 5 },
];

/* ------------------------------------------------------------
   Semilla de ejemplo (Industrias Aurea S.L.): la carga el botón "Cargar
   datos de ejemplo" del dashboard sobre un espacio vacío.
   ------------------------------------------------------------ */
const SEED_EJEMPLO = {
  departments: [
    { id: 1, name: "Recursos Humanos", description: "Departamento de Recursos Humanos", createdAt: "08/09/2021 12:03" },
    { id: 2, name: "Comercial y Marketing", description: "Departamento Comercial y Marketing", createdAt: "10/09/2021 11:13" },
    { id: 3, name: "Técnico", description: "Departamento Técnico", createdAt: "21/09/2021 11:44" },
    { id: 10, name: "Logística", description: "Departamento de Logística y Distribución", createdAt: "15/02/2022 10:21" },
  ],
  positions: [
    { id: 1, code: "P01", name: "Director/a Recursos Humanos", departmentId: 1, levelId: 1, reportsToId: null, headcount: 1,
      description: "Dirige la política de personas: selección, relaciones laborales, formación y negociación del convenio.",
      createdAt: "08/09/2021 12:04" },
    { id: 2, code: "P02", name: "Director/a Comercial", departmentId: 2, levelId: 1, reportsToId: null, headcount: 1,
      description: "Responsable de la cifra de negocio, la red comercial y la apertura de mercados de exportación.",
      createdAt: "10/09/2021 11:14" },
    { id: 3, code: "P03", name: "Administrativo/a", departmentId: 1, levelId: 4, reportsToId: 1, headcount: 4,
      description: "Tramitación de nóminas, contratos y gestión documental del área de personas.",
      createdAt: "21/09/2021 11:45" },
    { id: 4, code: "P04", name: "Jefe/a Almacén", departmentId: 10, levelId: 2, reportsToId: null, headcount: 1,
      description: "Organiza la recepción, el almacenaje y la expedición de material, y coordina al personal de planta.",
      createdAt: "22/09/2021 10:33" },
    { id: 5, code: "P05", name: "Técnico/a Soporte", departmentId: 3, levelId: 3, reportsToId: null, headcount: 3,
      description: "Atiende las incidencias de usuario y el mantenimiento del puesto de trabajo.",
      createdAt: "12/10/2021 09:22" },
    { id: 6, code: "P06", name: "Responsable Marketing Digital", departmentId: 2, levelId: 2, reportsToId: 2, headcount: 1,
      description: "Define y ejecuta el plan de marketing digital y la presencia de marca.",
      createdAt: "03/11/2021 14:10" },
    { id: 7, code: "P07", name: "Comercial Senior", departmentId: 2, levelId: 3, reportsToId: 6, headcount: 6,
      description: "Gestiona la cartera de clientes internacionales y negocia contratos de suministro.",
      createdAt: "12/01/2022 10:00" },
    { id: 8, code: "P08", name: "Mozo de Almacén", departmentId: 10, levelId: 5, reportsToId: 4, headcount: 22,
      description: "Carga, descarga y preparación de pedidos con carretilla elevadora.",
      createdAt: "14/03/2022 16:40" },
    { id: 9, code: "P09", name: "Técnico/a de Sistemas", departmentId: 3, levelId: 3, reportsToId: 5, headcount: 2,
      description: "Administra servidores, red y copias de seguridad de la planta y las oficinas.",
      createdAt: "20/05/2022 11:00" },
    { id: 1318, code: "P01318", name: "Conductor/a", departmentId: 10, levelId: 4, reportsToId: 4, headcount: 9,
      description: "Reparto de mercancía a clientes nacionales con vehículo de empresa.",
      createdAt: "20/10/2023 12:44" },
  ],
  /* Valoraciones ya cargadas de partida (puesto -> subfactor -> nivel). */
  valuations: {
    1: {
      "k-formacion": "L5", "k-idiomas": "L3", "k-liderazgo": "L3", "k-iniciativa": "L3", "k-maquinaria": "L0",
      "r-personas": "L3", "e-mental": "L3", "e-concentracion": "L2", "c-nocturnos": "L0", "c-viajes": "L1",
    },
    5: {
      "k-formacion": "L3", "k-idiomas": "L2", "k-liderazgo": "L1", "k-iniciativa": "L2", "k-maquinaria": "L2",
      "r-personas": "L0", "e-mental": "L2", "e-concentracion": "L2", "c-nocturnos": "L1", "c-viajes": "L0",
    },
    6: {
      "k-formacion": "L4", "k-idiomas": "L3", "k-liderazgo": "L2", "k-iniciativa": "L3", "k-maquinaria": "L0",
      "r-personas": "L1", "e-mental": "L3", "e-concentracion": "L2", "c-nocturnos": "L0", "c-viajes": "L1",
    },
    7: {
      "k-formacion": "L3", "k-idiomas": "L2", "k-liderazgo": "L1", "k-iniciativa": "L2", "k-maquinaria": "L0",
      "r-personas": "L0", "e-mental": "L2", "e-concentracion": "L2", "c-nocturnos": "L0", "c-viajes": "L2",
    },
    8: {
      "k-formacion": "L1", "k-idiomas": "L0", "k-liderazgo": "L0", "k-iniciativa": "L0", "k-maquinaria": "L1",
      "r-personas": "L0", "e-mental": "L0", "e-concentracion": "L1", "c-nocturnos": "L1", "c-viajes": "L0",
    },
    9: {
      "k-formacion": "L4", "k-idiomas": "L3", "k-liderazgo": "L1", "k-iniciativa": "L2", "k-maquinaria": "L1",
      "r-personas": "L0", "e-mental": "L2", "e-concentracion": "L2", "c-nocturnos": "L1", "c-viajes": "L0",
    },
  },
};

/* Cada espacio arranca con su propia copia de los catálogos, para que
   tocar los factores de una empresa no afecte a las demás. */
function buildCompany(seed) {
  return {
    metodo: "talention",
    departments: structuredClone(seed.departments || []),
    levels: structuredClone(LEVELS),
    positions: (seed.positions || []).map(p => ({
      area: "",
      centro: "",
      scaleOverrideId: null,
      scaleOverrideReason: "",
      ...structuredClone(p),
    })),
    factors: {
      groups: FACTOR_GROUPS.map(g => ({ ...g, svpt: g.defaultSVPT })),
      items: structuredClone(FACTOR_ITEMS),
      standardId: null,     // último estándar aplicado, si lo hay
    },
    scales: structuredClone(SCALES),
    valuations: structuredClone(seed.valuations || {}),
    ministerio: crearMinisterioVacio(),
  };
}

/* ------------------------------------------------------------
   Cálculo
   ------------------------------------------------------------ */

/* Puntos SVPT de un puesto a partir de sus respuestas.
     totalPoints = redondeo(
       suma por grupo(
         (grupo.svpt/100) * suma por subfactor seleccionado(
           peso_relativo * (índice_nivel / (nº_niveles - 1)) ) ) * 1000 )
   Con los pesos por defecto el máximo es 1000. */
function computePositionPoints(valuationByItem, factorsState) {
  if (!valuationByItem) return 0;
  let total = 0;
  for (const grp of factorsState.groups) {
    const items = factorsState.items[grp.id] || [];
    const selected = items.filter(it => it.selected && it.levels.length > 0);
    const groupTotalWeight = selected.reduce((s, it) => s + (it.weight || 0), 0) || 1;
    let groupScore = 0;
    for (const it of selected) {
      const chosen = valuationByItem[it.id];
      if (!chosen) continue;
      const idx = it.levels.findIndex(l => l.id === chosen);
      if (idx < 0) continue;
      const maxIdx = it.levels.length - 1;
      const ratio = maxIdx === 0 ? 0 : idx / maxIdx;
      // el peso se normaliza sobre el total del grupo, por si no suma 100
      const itemWeightRatio = it.weight / groupTotalWeight;
      groupScore += itemWeightRatio * ratio;
    }
    total += (grp.svpt / 100) * groupScore;
  }
  return Math.round(total * 1000);
}

function findScaleForPoints(points, scales) {
  return scales.find(s => points >= s.min && points <= s.max) || null;
}

/* Escala oficial de un puesto.

   Manda el override manual si lo hay; si no, la escala se deduce de la
   puntuación, y solo cuando la valoración está finalizada: con una
   valoración a medias los puntos aún no significan nada.

   Devuelve { scale, manual, reason }. */
function resolveScaleForPosition(position, scales) {
  if (!position) return { scale: null, manual: false, reason: "" };
  if (position.scaleOverrideId != null) {
    const forced = scales.find(s => s.id === position.scaleOverrideId);
    if (forced) return { scale: forced, manual: true, reason: position.scaleOverrideReason || "" };
  }
  if (position.status !== "FINALIZADA") return { scale: null, manual: false, reason: "" };
  return { scale: findScaleForPoints(position.points, scales), manual: false, reason: "" };
}

function countCompletedItems(valuationByItem, factorsState) {
  if (!valuationByItem) return { total: 0, done: 0 };
  let total = 0, done = 0;
  for (const grp of factorsState.groups) {
    const items = factorsState.items[grp.id] || [];
    for (const it of items) {
      if (it.selected && it.levels.length > 0) {
        total++;
        if (valuationByItem[it.id]) done++;
      }
    }
  }
  return { total, done };
}

/* Reparto del peso final entre subfactores con etiqueta de género.

   El peso efectivo de un subfactor es el porcentaje de la puntuación total
   que depende de él: (svpt del grupo) x (su peso dentro del grupo).
   La etiqueta no interviene en el cálculo; esto solo sirve para ver cuánta
   parte de la valoración descansa en factores marcados. */
function computeGenderBalance(factorsState) {
  const byGender = { NEUTRO: 0, MASCULINO: 0, FEMENINO: 0 };
  const items = [];
  for (const grp of factorsState.groups) {
    const groupItems = (factorsState.items[grp.id] || []).filter(it => it.selected && it.levels.length > 0);
    const groupWeight = groupItems.reduce((s, it) => s + (it.weight || 0), 0);
    for (const it of groupItems) {
      const share = groupWeight > 0 ? (it.weight || 0) / groupWeight : 0;
      const effective = grp.svpt * share;
      const gender = byGender[it.gender] !== undefined ? it.gender : "NEUTRO";
      byGender[gender] += effective;
      if (gender !== "NEUTRO") items.push({ ...it, groupId: grp.id, groupName: grp.short, effective });
    }
  }
  const total = byGender.NEUTRO + byGender.MASCULINO + byGender.FEMENINO;
  const pct = (v) => (total > 0 ? (v / total) * 100 : 0);
  return {
    total,
    neutral: pct(byGender.NEUTRO),
    masculine: pct(byGender.MASCULINO),
    feminine: pct(byGender.FEMENINO),
    gendered: pct(byGender.MASCULINO + byGender.FEMENINO),
    // El peso de cada subfactor va normalizado sobre el mismo total que los
    // porcentajes de arriba: si no, con los pesos de grupo sin cuadrar a 100
    // las filas no sumarían el titular.
    items: items
      .map(it => ({ ...it, effective: pct(it.effective) }))
      .sort((a, b) => b.effective - a.effective),
  };
}

/* Quita las respuestas que apuntan a un nivel que ya no existe.
   Hace falta al editar los niveles de un subfactor o al aplicar un estándar:
   si no, un puesto se daría por valorado con una respuesta fantasma. */
function pruneValuations(valuations, factorsState) {
  const validLevels = new Map();
  for (const grp of factorsState.groups) {
    for (const it of factorsState.items[grp.id] || []) {
      validLevels.set(it.id, new Set(it.levels.map(l => l.id)));
    }
  }
  const cleaned = {};
  let removed = 0;
  for (const [positionId, answers] of Object.entries(valuations)) {
    const kept = {};
    for (const [itemId, levelId] of Object.entries(answers)) {
      const levels = validLevels.get(itemId);
      if (levels && levels.has(levelId)) kept[itemId] = levelId;
      else removed++;
    }
    cleaned[positionId] = kept;
  }
  return { valuations: cleaned, removed };
}

/* Aplica un estándar sobre la configuración de factores.

   Sustituye los pesos de grupo y, dentro de cada grupo, deja seleccionados
   solo los subfactores del estándar con su peso. Un estándar puede además
   traer los niveles de un subfactor que aún no los tenía definidos. */
function applyStandardToFactors(factorsState, standard) {
  const groups = factorsState.groups.map(g => ({
    ...g,
    svpt: standard.groups[g.id] ?? g.svpt,
  }));

  const items = {};
  for (const groupId of Object.keys(factorsState.items)) {
    const wanted = new Map((standard.items[groupId] || []).map(it => [it.id, it]));
    items[groupId] = factorsState.items[groupId].map(it => {
      const spec = wanted.get(it.id);
      if (!spec) return { ...it, selected: false, weight: 0 };
      return {
        ...it,
        selected: true,
        weight: spec.weight,
        type: spec.type || it.type,
        levels: spec.levels ? structuredClone(spec.levels) : it.levels,
      };
    });
  }

  return { ...factorsState, groups, items, standardId: standard.id };
}

/* ------------------------------------------------------------
   Espacios y viaje al servidor
   ------------------------------------------------------------ */

/* Versión de la forma del espacio que se guarda en el servidor (columna
   `esquema` de svpt_espacios). Subirla exige un paso en migrarEspacio.
   v2: método de valoración, bloque `ministerio` y área/centro por puesto. */
const ESQUEMA_ACTUAL = 2;

const METODOS_VALORACION = ["talention", "ministerio"];

/* Espacio con el que arranca una empresa que aún no tiene ninguno. */
function crearEspacioVacio() {
  return buildCompany({ departments: [], positions: [], valuations: {} });
}

/* Espacio de demostración, para enseñar el apartado con contenido. */
function espacioDeEjemplo() {
  return buildCompany(SEED_EJEMPLO);
}

/* Lo que viaja al servidor: solo datos fuente. Los puntos, el estado y el
   aviso de revisión de cada puesto se recalculan al cargar (store.jsx). */
function serializarEspacio(space) {
  return {
    metodo: space.metodo,
    departments: space.departments,
    levels: space.levels,
    positions: space.positions.map(({ points, status, review, ...fuente }) => fuente),
    factors: space.factors,
    scales: space.scales,
    valuations: space.valuations,
    ministerio: space.ministerio,
  };
}

/* PHP no distingue {} de [] al decodificar JSON: un objeto vacío vuelve del
   servidor como lista vacía. Las pantallas indexan por clave, así que se
   recompone como objeto. Una lista CON elementos donde se espera un objeto no
   es ese caso: es el contrato roto, y tragárselo costaría los datos que trae. */
function comoObjeto(valor, contexto) {
  if (!Array.isArray(valor)) return valor || {};
  if (valor.length === 0) return {};
  throw new Error(`El servidor devolvió una lista donde ${contexto} debe ser un objeto indexado por clave. No se carga el espacio para no perder datos.`);
}

/* Bloque Ministerio tal como llega del servidor, a la forma actual. Los
   «Otros» que falten se rellenan vacíos; uno activo pero incompleto se
   carga inactivo (no puede puntuar sin sus textos) y la poda se lleva sus
   respuestas, igual que las que no encajen en el catálogo. Devuelve también
   cuántas respuestas ha quitado la poda, para avisar al cargar. */
function normalizarMinisterio(ministerio) {
  if (!ministerio || typeof ministerio !== "object") {
    throw new Error('El espacio SVPT llegó con "ministerio" en un formato inesperado. No se carga para no perder datos.');
  }
  const bloque = comoObjeto(ministerio, "ministerio");
  const base = crearMinisterioVacio();
  const guardados = comoObjeto(bloque.otros, "ministerio.otros");
  const otros = {};
  for (const codigo of CODIGOS_OTROS) {
    const guardado = comoObjeto(guardados[codigo], `el factor ${codigo}`);
    const leido = typeof guardado === "object" ? guardado : {};
    const niveles = Array.isArray(leido.niveles) && leido.niveles.length === 5
      ? leido.niveles.map(t => (typeof t === "string" ? t : ""))
      : ["", "", "", "", ""];
    const otro = {
      activo: leido.activo === true,
      nombre: typeof leido.nombre === "string" ? leido.nombre : "",
      justificacion: typeof leido.justificacion === "string" ? leido.justificacion : "",
      niveles,
    };
    otros[codigo] = otro.activo === true && !otroCompleto(otro) ? { ...otro, activo: false } : otro;
  }
  const respuestas = {};
  for (const [positionId, porCodigo] of Object.entries(comoObjeto(bloque.respuestas, "ministerio.respuestas"))) {
    respuestas[positionId] = comoObjeto(porCodigo, `las respuestas Ministerio del puesto ${positionId}`);
  }
  const podadas = podarRespuestasMinisterio(respuestas, otros);
  return { ministerio: { otros, respuestas: podadas.respuestas }, respuestasDescartadas: podadas.removed };
}

/* Adapta un espacio guardado a la forma actual. Acepta la v1 (solo método
   Talention) y la v2; lo desconocido no se carga para no sobrescribirlo.
   Devuelve { space, respuestasDescartadas }: lo segundo son las respuestas
   Ministerio que la poda ha quitado, para que quien carga lo avise. */
function migrarEspacioConAviso(datos, esquema) {
  if (esquema !== 1 && esquema !== ESQUEMA_ACTUAL) {
    throw new Error(`Esquema de espacio SVPT desconocido: ${esquema} (el actual es ${ESQUEMA_ACTUAL}). Actualiza la aplicación.`);
  }
  // El provider que carga esto envuelve toda la app (§7.1): un blob mal
  // formado no puede reventar dentro de calcularPosiciones, porque ahí ya
  // no hay quien lo atrape y se lleva por delante Registro entero. Se
  // valida la forma aquí, a la entrada, para que el fallo aterrice como
  // cargaError en vez de como pantalla en blanco.
  const esArray = (v) => Array.isArray(v);
  for (const [clave, valido] of [["departments", esArray], ["levels", esArray], ["positions", esArray], ["scales", esArray]]) {
    if (!valido(datos[clave])) throw new Error(`El espacio SVPT llegó con "${clave}" en un formato inesperado. No se carga para no perder datos.`);
  }
  if (!datos.factors || typeof datos.factors !== "object" || Array.isArray(datos.factors)) {
    throw new Error('El espacio SVPT llegó con "factors" en un formato inesperado. No se carga para no perder datos.');
  }
  const valuations = {};
  for (const [positionId, answers] of Object.entries(comoObjeto(datos.valuations, "valuations"))) {
    valuations[positionId] = comoObjeto(answers, `la valoración del puesto ${positionId}`);
  }
  const base = {
    ...datos,
    factors: { ...datos.factors, items: comoObjeto(datos.factors?.items, "factors.items") },
    valuations,
  };
  if (esquema === 1) {
    return {
      space: {
        ...base,
        metodo: "talention",
        positions: base.positions.map(p => ({ ...p, area: p.area ?? "", centro: p.centro ?? "" })),
        ministerio: crearMinisterioVacio(),
      },
      respuestasDescartadas: 0,
    };
  }
  if (!METODOS_VALORACION.includes(datos.metodo)) {
    throw new Error(`El espacio SVPT llegó con un método de valoración desconocido ("${datos.metodo}"). No se carga para no perder datos.`);
  }
  const { ministerio, respuestasDescartadas } = normalizarMinisterio(datos.ministerio);
  return { space: { ...base, ministerio }, respuestasDescartadas };
}

function migrarEspacio(datos, esquema) {
  return migrarEspacioConAviso(datos, esquema).space;
}

/* ------------------------------------------------------------
   Clasificación y progreso según el método de la empresa. Las pantallas
   compartidas (dashboard, puestos, lista de valoración) usan esto y no
   saben si hablan de escalas o de agrupaciones.
   ------------------------------------------------------------ */

const deEscala = (scale) => ({
  id: scale.id, etiqueta: scale.name, detalle: scale.label, color: scale.color,
  rango: `Rango ${scale.min} – ${scale.max} pts`, manual: false, motivo: "",
});

/* Clasificación oficial de un puesto: su escala (la fijada a mano manda) o,
   en Ministerio, su agrupación, solo con la valoración finalizada. */
function clasificacionDelPuesto(puesto, espacio) {
  if (!puesto) return null;
  if (espacio.metodo === "ministerio") {
    return puesto.status === "FINALIZADA" ? clasificacionDePuntos(puesto.points, espacio) : null;
  }
  const { scale, manual, reason } = resolveScaleForPosition(puesto, espacio.scales);
  return scale ? { ...deEscala(scale), manual, motivo: reason } : null;
}

/* Clasificación que corresponde a unos puntos sueltos (la media del dashboard). */
function clasificacionDePuntos(puntos, espacio) {
  if (espacio.metodo === "ministerio") {
    const tramo = agrupacionMinisterio(puntos);
    if (!tramo) return null;
    const rango = rangoDeTramo(tramo);
    return { id: tramo.agrupacion, etiqueta: `Agrupación ${tramo.agrupacion}`, detalle: rango, color: tramo.color, rango, manual: false, motivo: "" };
  }
  const scale = findScaleForPoints(puntos, espacio.scales);
  return scale ? deEscala(scale) : null;
}

function progresoDelPuesto(puesto, espacio) {
  if (espacio.metodo === "ministerio") {
    return progresoMinisterio(espacio.ministerio.respuestas[puesto.id], espacio.ministerio.otros);
  }
  return countCompletedItems(espacio.valuations[puesto.id], espacio.factors);
}

export const TALENTION_HELPERS = {
  computePositionPoints,
  findScaleForPoints,
  resolveScaleForPosition,
  countCompletedItems,
  computeGenderBalance,
  pruneValuations,
  applyStandardToFactors,
  FACTOR_GROUPS,
  FACTOR_STANDARDS,
  ITEM_TYPES,
  GENDERS,
  GENERIC_LEVELS,
  BINARY_LEVELS,
};

export {
  FACTOR_GROUPS,
  FACTOR_ITEMS,
  FACTOR_STANDARDS,
  ITEM_TYPES,
  GENDERS,
  GENERIC_LEVELS,
  BINARY_LEVELS,
  SCALES,
  LEVELS,
  computePositionPoints,
  findScaleForPoints,
  resolveScaleForPosition,
  countCompletedItems,
  computeGenderBalance,
  pruneValuations,
  applyStandardToFactors,
  ESQUEMA_ACTUAL,
  METODOS_VALORACION,
  clasificacionDelPuesto,
  clasificacionDePuntos,
  progresoDelPuesto,
  crearEspacioVacio,
  espacioDeEjemplo,
  serializarEspacio,
  migrarEspacio,
  migrarEspacioConAviso,
};
